import React from "react";
import {
    Card,
    Typography,
    Space,
    Tag,
    Button,
    Table,
    Badge,
    Tooltip,
    Alert,
    Divider,
    Empty,
    message,
} from "antd";
import {
    FiCreditCard,
    FiCalendar,
    FiDollarSign,
    FiDownload,
    FiFileText,
    FiRefreshCw,
    FiShield,
    FiLayers,
    FiGrid,
    FiTarget,
    FiBell,
    FiUser,
    FiClock,
    FiAlertTriangle,
} from "react-icons/fi";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import Layout from "../../components/layout/Layout";

const { Title, Text } = Typography;


const demoPaymentMethod = null;

const demoNextInvoice = null;
// const demoNextInvoice = {
//   number: "INV-2025-0915-001",
//   date: new Date(Date.now() + 1000 * 60 * 60 * 24 * 4).toISOString(), // +4 days
//   amount: 19000, // cents
//   currency: "USD",
//   period: "Yearly",
//   items: [{ name: "Pro Plan — Yearly", qty: 1, amount: 19000 }],
// };

const demoInvoices = [
    {
        id: "inv_1009",
        index: 1,
        number: "INV-2025-0901-002",
        date: "2025-09-01T08:16:00.000Z",
        amount: 19000,
        currency: "USD",
        status: "paid",
        period: "Yearly",
        link: "#",
        pdf: "#",
    },
    {
        id: "inv_1008",
        index: 2,
        number: "INV-2024-0901-001",
        date: "2024-09-01T10:21:00.000Z",
        amount: 19000,
        currency: "USD",
        status: "paid",
        period: "Yearly",
        link: "#",
        pdf: "#",
    },
    {
        id: "inv_1007",
        index: 3,
        number: "INV-2024-0501-007",
        date: "2024-05-01T13:01:00.000Z",
        amount: 1900,
        currency: "USD",
        status: "refunded",
        period: "Monthly",
        link: "#",
        pdf: "#",
    },
    {
        id: "inv_1006",
        index: 4,
        number: "INV-2024-0401-006",
        date: "2024-04-01T10:01:00.000Z",
        amount: 1900,
        currency: "USD",
        status: "failed",
        period: "Monthly",
        link: "#",
        pdf: "#",
    },
];

function Subnav() {
    const LINKS = [
        { to: "/dashboard", label: "Dashboard", Icon: FiGrid },
        { to: "/my/predictions", label: "My predictions", Icon: FiTarget },
        { to: "/notifications", label: "Notifications", Icon: FiBell },
        { to: "/account/profile", label: "My profile", Icon: FiUser },
        { to: "/account/billing", label: "Billing", Icon: FiCreditCard },
        { to: "/plans", label: "Plans & Pricing", Icon: FiLayers },
        { to: "/account/security", label: "Change password", Icon: FiShield },
    ];
    const { pathname } = useLocation();
    const isActive = (to) => pathname === to || (to !== "/" && pathname.startsWith(to));

    return (
        <nav className="px-subnav" aria-label="Account navigation">
            <div className="px-subnav__inner">
                {LINKS.map(({ to, label, Icon }) => (
                    <Link key={to} to={to} className={`px-subnav__link ${isActive(to) ? "is-active" : ""}`}>
                        <Icon aria-hidden />
                        <span>{label}</span>
                    </Link>
                ))}
            </div>
        </nav>
    );
}

export default function Billing() {
    const member = useSelector((s) => s?.Member?.data);
    const paymentMethod =
        member?.billing?.paymentMethod || demoPaymentMethod || {
            brand: "visa",
            last4: "4242",
            expMonth: 8,
            expYear: 2027,
            isDefault: true,
        };

    const nextInvoice = member?.billing?.nextInvoice || demoNextInvoice;
    const invoices = member?.billing?.history || demoInvoices;

    const onUpdatePayment = () => {
        message.loading({ key: "pm", content: "Opening secure payment method update…" });
        setTimeout(() => {
            message.success({ key: "pm", content: "Payment method updated.", duration: 2 });
        }, 1000);
    };

    const onDownload = (row) => {
        message.success(`Downloading PDF for ${row.number}`);
    };

    const onView = (row) => {
        message.info(`Opening invoice ${row.number}`);
    };

    const columns = [
        {
            title: "#",
            dataIndex: "index",
            key: "number",
            align: 'center',
            render: (v) => <Text strong>{v}.</Text>,
            ellipsis: true,
        },
        {
            title: "Invoice",
            dataIndex: "number",
            key: "number",
            render: (v) => <Text strong>{v}</Text>,
            ellipsis: true,
        },
        {
            title: "Date",
            dataIndex: "date",
            key: "date",
            responsive: ["sm"],
            render: (iso) => formatDateTime(iso),
        },
        {
            title: "Period",
            dataIndex: "period",
            key: "period",
            width: 120,
            responsive: ["md"],
            render: (p) => <Tag color="geekblue">{p}</Tag>,
        },
        {
            title: "Amount",
            dataIndex: "amount",
            key: "amount",
            align: "right",
            render: (cents, row) => <strong>{formatMoney(cents, row.currency)}</strong>,
        },
        {
            title: "Status",
            dataIndex: "status",
            align: "center",
            key: "status",
            width: 120,
            render: (s) => statusTag(s),
        },
        {
            title: "",
            key: "actions",
            width: 140,
            align: "right",
            render: (_, row) => (
                <Space>
                    <Tooltip title="Download PDF">
                        <Button icon={<FiDownload />} onClick={() => onDownload(row)} />
                    </Tooltip>
                    <Tooltip title="View details">
                        <Button icon={<FiFileText />} onClick={() => onView(row)} />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    const ui = {
        heroWrap: {
            position: "relative",
            padding: "80px 16px 80px",
            background:
                "radial-gradient(1200px 280px at 50% -30%, rgba(59,130,246,0.25), transparent 60%), linear-gradient(180deg, #0b1020 0%, #0b1020 40%, #0c1428 100%)",
            color: "white",
            borderBottom: "1px solid rgba(255,255,255,.06)",
        },
        heroInner: { maxWidth: 1350, margin: "0 auto" },
        badge: {
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            fontSize: 12,
            color: "#93c5fd",
            background: "rgba(147,197,253,.10)",
            border: "1px solid rgba(147,197,253,.30)",
            padding: "6px 12px",
            borderRadius: 999,
        },
        heroTitle: { margin: "14px 0 10px", fontSize: 36, lineHeight: 1.15, fontWeight: 900, color: "#fff", letterSpacing: "-0.02em" },
        heroSub: { margin: 0, color: "rgba(255,255,255,.85)", maxWidth: 820 },
    };

    return (
        <Layout headerStyle={1} footerStyle={1} breadcrumbTitle="Billing">
            <section style={ui.heroWrap} aria-label="Hero">
                <div style={ui.heroInner}>
                    <span style={ui.badge}><FiCreditCard /> Billing</span>
                    <h1 style={ui.heroTitle}>Manage your billing & invoices</h1>
                    <p style={ui.heroSub}>
                        Update your payment method, review upcoming charges, and download past invoices.
                    </p>
                </div>
            </section>

            <Subnav />

            <section className="px-billing">
                <div className="px-wrap">
                    <aside className="px-side">
                        <Card className="px-card" bordered>
                            <Space direction="vertical" style={{ width: "100%" }} size={10}>
                                <Title level={4} style={{ margin: 0 }}>
                                    Payment method
                                </Title>
                                <div className="px-pm">
                                    <div className="px-pm__brand">
                                        <Badge color="#1e40af" />
                                        <Text strong style={{ textTransform: "uppercase" }}>{paymentMethod.brand}</Text>
                                    </div>
                                    <div className="px-pm__meta">
                                        <div>•••• •••• •••• {paymentMethod.last4}</div>
                                        <div className="muted">Exp {two(paymentMethod.expMonth)}/{String(paymentMethod.expYear).slice(-2)}</div>
                                    </div>
                                    {paymentMethod.isDefault && <Tag color="blue">Default</Tag>}
                                </div>
                                <Space>
                                    <Button type="primary" onClick={onUpdatePayment}>
                                        Update payment method
                                    </Button>
                                    <Button>
                                        <Link to="/plans">
                                            Change plan
                                        </Link>
                                    </Button>
                                </Space>
                            </Space>

                            <Divider style={{ margin: "16px 0" }} />

                            <Title level={5} style={{ marginTop: 0 }}>
                                {nextInvoice ? "Next invoice" : "No upcoming invoice"}
                            </Title>

                            {nextInvoice ? (
                                <div className="px-next">
                                    <div className="px-next__row">
                                        <span><FiCalendar /> Date</span>
                                        <span>{formatDate(nextInvoice.date)}</span>
                                    </div>
                                    <div className="px-next__row">
                                        <span><FiDollarSign /> Amount</span>
                                        <span><strong>{formatMoney(nextInvoice.amount, nextInvoice.currency)}</strong></span>
                                    </div>
                                    <div className="px-next__row">
                                        <span><FiRefreshCw /> Billing cycle</span>
                                        <span>{nextInvoice.period}</span>
                                    </div>
                                    <Divider style={{ margin: "12px 0" }} />
                                    <ul className="px-items">
                                        {nextInvoice.items?.map((it, idx) => (
                                            <li key={idx}>
                                                <span>{it.name}</span>
                                                <span>{it.qty} × {formatMoney(it.amount, nextInvoice.currency)}</span>
                                            </li>
                                        ))}
                                    </ul>
                                    <Alert
                                        type="info"
                                        showIcon
                                        style={{ marginTop: 10 }}
                                        message="Heads up"
                                        description="You can change or cancel your subscription before the renewal date."
                                    />
                                </div>
                            ) : (
                                <Empty
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    description="Nothing scheduled. You’ll see your upcoming invoice here."
                                />
                            )}
                        </Card>
                    </aside>

                    <main className="px-main">
                        <Card className="px-card" title="Invoice history" extra={<SmallLegend />}>
                            <Card style={{ borderRadius: 0 }} bodyStyle={{ padding: 0 }}>
                                <Table
                                    rowKey="id"
                                    size="small"
                                    dataSource={invoices}
                                    columns={columns}
                                    pagination={{ pageSize: 6, showSizeChanger: false }}
                                    scroll={{ x: true }}
                                />
                            </Card>
                        </Card>
                    </main>
                </div>

                <style>{`
          .px-billing { background:#f7f7f9; padding:30px; }
          .px-wrap {
            max-width: 1400px; margin: 0 auto;
            display: grid; gap: 20px;
            grid-template-columns: 420px 1fr;
          }
          @media (max-width: 1024px) {
            .px-wrap { grid-template-columns: 1fr; }
          }
          .px-card { border-radius:16px; }

          .px-pm {
            display:flex; align-items:center; gap:12px; flex-wrap:wrap;
            padding:10px; border:1px solid rgba(0,0,0,.08);
            border-radius:12px; background:#fff;
          }
          .px-pm__brand { display:flex; align-items:center; gap:8px; }
          .px-pm__meta { display:flex; gap:10px; color:#334155; }
          .muted { color:#64748b; }

          .px-next { display:grid; gap:8px; }
          .px-next__row { display:flex; align-items:center; justify-content:space-between; color:#334155; }
          .px-next__row span:first-child { display:flex; gap:8px; align-items:center; color:#475569; }

          .px-items { list-style:none; padding:0; margin:0; display:grid; gap:6px; font-size:13px; color:#334155; }
          .px-items li { display:flex; align-items:center; justify-content:space-between; }

          /* Account subnav (shared style) */
          .px-subnav {
            position: sticky; top: 0; z-index: 20;
            backdrop-filter: blur(6px);
            background: rgba(255,255,255,.85);
            border-bottom: 1px solid rgba(2,6,23,.08);
          }
          .px-subnav__inner {
            max-width: 1400px; margin: 0 auto;
            padding: 10px 16px;
            display: flex; gap: 8px; align-items: center; flex-wrap: nowrap;
            overflow-x: auto; -webkit-overflow-scrolling: touch;
          }
          .px-subnav__link {
            display: inline-flex; align-items: center;
            padding: 8px 12px; gap: 8px;
            border-radius: 999px;
            font-size: 13px; font-weight: 700;
            color: #334155;
            border: 1px solid transparent;
            white-space: nowrap;
          }
          .px-subnav__link:hover {
            background: #f1f5f9;
            border-color: rgba(2,6,23,.08);
          }
          .px-subnav__link.is-active {
            color: #1e3a8a;
            background: rgba(37,99,235,.10);
            border-color: rgba(37,99,235,.25);
          }
          @media (max-width: 480px) {
            .px-subnav__inner { gap: 6px; padding: 8px 12px; }
            .px-subnav__link { padding: 6px 10px; font-weight: 600; }
          }
        `}</style>
            </section>
        </Layout>
    );
}

function SmallLegend() {
    return (
        <Space size={12} wrap>
            <LegendItem color="green" label="Paid" />
            <LegendItem color="gold" label="Due" />
            <LegendItem color="red" label="Failed" />
            <LegendItem color="blue" label="Refunded" />
        </Space>
    );
}
function LegendItem({ color, label }) {
    return (
        <Space size={6}>
            <Badge color={color} />
            <Text type="secondary" style={{ fontSize: 12 }}>{label}</Text>
        </Space>
    );
}

function statusTag(status) {
    const map = {
        paid: { color: "green", icon: <FiDollarSign /> },
        due: { color: "gold", icon: <FiClock /> },
        failed: { color: "red", icon: <FiAlertTriangle /> },
        refunded: { color: "blue", icon: <FiRefreshCw /> },
    };
    const { color } = map[status] || { color: "default", icon: null };
    return <Tag color={color} style={{ textTransform: "capitalize" }}>{status}</Tag>;
}

function two(n) {
    return String(n).padStart(2, "0");
}
function formatMoney(cents = 0, currency = "USD") {
    const amount = (cents ?? 0) / 100;
    try {
        return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(amount);
    } catch {
        return `$${amount.toFixed(2)}`;
    }
}
function formatDate(iso) {
    try {
        const d = new Date(iso);
        return d.toLocaleDateString([], { year: "numeric", month: "short", day: "2-digit" });
    } catch {
        return "—";
    }
}
function formatDateTime(iso) {
    try {
        const d = new Date(iso);
        return d.toLocaleString([], {
            year: "numeric", month: "short", day: "2-digit",
            hour: "2-digit", minute: "2-digit",
        });
    } catch {
        return "—";
    }
}
